import { h } from 'preact';

import Router from 'preact-router';
import { useCallback } from 'preact/hooks';
import { useDispatch } from 'react-redux';
import { memo } from 'preact/compat';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';

import FrontEndLazyLoader from '../frontEndLazyLoader';

import LazyProperties from '../../routes/properties/lazy.jsx';
import Properties from '../../routes/properties';

import Organization from '../../routes/organization/index.jsx';
import LazyOrganization from '../../routes/organization/lazy.jsx';

import PropertyCruxUrl from '../../routes/propertyCruxUrl/index.jsx';
import LazyPropertyCruxUrl from '../../routes/propertyCruxUrl/lazy.jsx';

import PropertyCruxOrigin from '../../routes/propertyCruxOrigin/index.jsx';
import LazyPropertyCruxOrigin from '../../routes/propertyCruxOrigin/lazy.jsx';

import LcpIssueList from '../../routes/lcpIssueList/index.jsx';
import LazyLcpIssueList from '../../routes/lcpIssueList/lazy.jsx';

import LcpIssueDetails from '../../routes/lcpIssueDetails/index.jsx';
import LazyLcpIssueDetails from '../../routes/lcpIssueDetails/lazy.jsx';

import LcpUrlIssueDetails from '../../routes/lcpUrlIssueDetails/index.jsx';
import LazyLcpUrlIssueDetails from '../../routes/lcpUrlIssueDetails/lazy.jsx';

import InpIssueList from '../../routes/inpIssueList/index.jsx';
import LazyInpIssueList from '../../routes/inpIssueList/lazy.jsx';

import InpIssueDetails from '../../routes/inpIssueDetails/index.jsx';
import LazyInpIssueDetails from '../../routes/inpIssueDetails/lazy.jsx';

import InpUrlIssueDetails from '../../routes/inpUrlIssueDetails/index.jsx';
import LazyInpUrlIssueDetails from '../../routes/inpUrlIssueDetails/lazy.jsx';

import RpmOriginOverview from '../../routes/rpmOriginOverview/index.jsx';
import LazyRpmOriginOverview from '../../routes/rpmOriginOverview/lazy.jsx';

import RpmOriginDetailed from '../../routes/rpmOriginDetailed/index.jsx';
import LazyRpmOriginDetailed from '../../routes/rpmOriginDetailed/lazy.jsx';

import RpmUrlDetails from '../../routes/rpmUrlDetails/index.jsx';
import LazyRpmUrlDetails from '../../routes/rpmUrlDetails/lazy.jsx';

import ClsIssueList from '../../routes/clsIssueList/index.jsx';
import LazyClsIssueList from '../../routes/clsIssueList/lazy.jsx';

import ClsUrlIssueDetails from '../../routes/clsUrlIssueDetails/index.jsx';
import LazyClsUrlIssueDetails from '../../routes/clsUrlIssueDetails/lazy.jsx';

import ClsIssueDetails from '../../routes/clsIssueDetails/index.jsx';
import LazyClsIssueDetails from '../../routes/clsIssueDetails/lazy.jsx';

import AdminUserList from '../../routes/adminUserList/index.jsx';
import LazyAdminUserList from '../../routes/adminUserList/lazy.jsx';

import PropertyOverview from '../../routes/propertyOverview/index.jsx';
import LazyPropertyOverview from '../../routes/propertyOverview/lazy.jsx';

import IssueList from '../../routes/issues/index.jsx';
import LazyIssueList from '../../routes/issues/lazy.jsx';

import Search from '../../routes/search/index.jsx';
import LazySearch from '../../routes/search/lazy.jsx';

import { ON_RPM, PUBLIC_FOLDER } from '../../consts.js';
import CruxRoot from '../../routes/cruxRoot/index.jsx';
import { actions as navigationActions } from '../../store/state/navigation.js';
import OnRunner from '../conditionals/onRunner.jsx';
import RunnerAppealMessage from '../runnerAppealMessage.jsx';
import RpmAppealMessage from '../rpmAppealMessage.jsx';
import OnRpm from '../conditionals/onRpm.jsx';

function getRoutes() {
  if (ON_RPM) {
    return [
      <FrontEndLazyLoader
        path={`${PUBLIC_FOLDER}/organization/:organizationId`}
        LL_Component={Organization}
        LL_Fallback={LazyOrganization}
      />,
      <FrontEndLazyLoader
        path={`${PUBLIC_FOLDER}/adminUserList`}
        LL_Component={AdminUserList}
        LL_Fallback={LazyAdminUserList}
      />,
    ];
  } else {
    return [];
  }
}

const SCOPED_ROUTES = getRoutes();

function Main(_props) {
  const dispatch = useDispatch();

  const onRouteChange = useCallback(
    function onRouteChange(routeInfo) {
      const [active] = routeInfo.active;
      if (!active) {
        dispatch(navigationActions.CLEAR_PARAMS());
        return;
      }

      const { propertyId, organizationId, date, signature, url } = active.props;

      dispatch(
        navigationActions.NEW_PARAMS({
          propertyId,
          organizationId,
          date,
          signature,
          url,
        })
      );
    },
    [dispatch]
  );

  return (
    <Box
      component="main"
      sx={{
        backgroundColor: theme =>
          theme.palette.mode === 'light'
            ? theme.palette.grey[100]
            : theme.palette.grey[900],
        flexGrow: 1,
        height: '100vh',
        overflow: 'auto',
      }}
    >
      <Toolbar />
      <Container maxWidth="lg" sx={{ mt: 0, mb: 4 }}>
        <OnRunner>
          <RunnerAppealMessage />
        </OnRunner>

        <OnRpm>
          <RpmAppealMessage />
        </OnRpm>
      </Container>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Router onChange={onRouteChange}>
          {SCOPED_ROUTES}

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/`}
            LL_Component={CruxRoot}
            LL_Fallback={LazyPropertyOverview}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/properties`}
            LL_Component={Properties}
            LL_Fallback={LazyProperties}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/crux/url/:url`}
            LL_Component={PropertyCruxUrl}
            LL_Fallback={LazyPropertyCruxUrl}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/crux/origin`}
            LL_Component={PropertyCruxOrigin}
            LL_Fallback={LazyPropertyCruxOrigin}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/issues`}
            LL_Component={IssueList}
            LL_Fallback={LazyIssueList}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/lcp/issue/:date/:signature`}
            LL_Component={LcpIssueDetails}
            LL_Fallback={LazyLcpIssueDetails}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/lcp/urlissue/:date/:url`}
            LL_Component={LcpUrlIssueDetails}
            LL_Fallback={LazyLcpUrlIssueDetails}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/lcp`}
            LL_Component={LcpIssueList}
            LL_Fallback={LazyLcpIssueList}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/inp/issue/:date/:signature`}
            LL_Component={InpIssueDetails}
            LL_Fallback={LazyInpIssueDetails}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/inp/urlissue/:date/:url`}
            LL_Component={InpUrlIssueDetails}
            LL_Fallback={LazyInpUrlIssueDetails}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/inp`}
            LL_Component={InpIssueList}
            LL_Fallback={LazyInpIssueList}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/cls/issue/:date/:signature`}
            LL_Component={ClsIssueDetails}
            LL_Fallback={LazyClsIssueDetails}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/cls/urlissue/:date/:url`}
            LL_Component={ClsUrlIssueDetails}
            LL_Fallback={LazyClsUrlIssueDetails}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/cls`}
            LL_Component={ClsIssueList}
            LL_Fallback={LazyClsIssueList}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/realtime/origin/:frequency`}
            LL_Component={RpmOriginDetailed}
            LL_Fallback={LazyRpmOriginDetailed}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/realtime/url/:frequency/:url`}
            LL_Component={RpmUrlDetails}
            LL_Fallback={LazyRpmUrlDetails}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/realtime`}
            LL_Component={RpmOriginOverview}
            LL_Fallback={LazyRpmOriginOverview}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/overview`}
            LL_Component={PropertyOverview}
            LL_Fallback={LazyPropertyOverview}
          />

          <FrontEndLazyLoader
            path={`${PUBLIC_FOLDER}/property/:propertyId/search`}
            LL_Component={Search}
            LL_Fallback={LazySearch}
          />
        </Router>
      </Container>
    </Box>
  );
}

export default memo(Main);
